import React, { useRef, useState } from "react";
import { Upload, Loader2, X, Plus } from "lucide-react";
import { uploadFile, fileUrl, formatApiError } from "@/lib/api";
import { toast } from "sonner";

// Uploads multiple images; reports the ordered list of stored paths via onChange(urls).
export function MultiImageUpload({ value = [], onChange, max = 6, testid }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const images = value.filter(Boolean);

  const pick = () => inputRef.current?.click();

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const room = max - images.length;
      const uploaded = [];
      for (const f of files.slice(0, room)) {
        const res = await uploadFile(f);
        uploaded.push(res.url);
      }
      onChange([...images, ...uploaded]);
      toast.success(`${uploaded.length} photo(s) added`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = (i) => onChange(images.filter((_, idx) => idx !== i));
  const makeCover = (i) => {
    const next = [...images];
    const [pick] = next.splice(i, 1);
    onChange([pick, ...next]);
  };

  return (
    <div>
      <input ref={inputRef} type="file" accept="image/*" multiple onChange={onFiles} className="hidden" data-testid={testid ? `${testid}-input` : undefined} />
      <div className="grid grid-cols-3 gap-2">
        {images.map((url, i) => (
          <div key={i} className="relative group aspect-[4/3] rounded-lg overflow-hidden border border-[#232A4D]" data-testid={`${testid}-thumb-${i}`}>
            <img src={fileUrl(url)} alt="" className="w-full h-full object-cover" />
            {i === 0 && <span className="absolute bottom-1 left-1 text-[9px] bg-cyan-500/80 text-white rounded px-1.5 py-0.5">Cover</span>}
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
              {i !== 0 && <button type="button" onClick={() => makeCover(i)} className="text-[9px] bg-white/20 text-white rounded px-1.5 py-1">Cover</button>}
              <button type="button" onClick={() => remove(i)} className="w-6 h-6 rounded-full bg-[#FF5E7E] text-white flex items-center justify-center"><X className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        ))}
        {images.length < max && (
          <button type="button" onClick={pick} disabled={busy} data-testid={testid}
            className="aspect-[4/3] rounded-lg border border-dashed border-[#2A3350] bg-[#0B0E1B] text-slate-400 hover:border-cyan-400/50 hover:text-cyan-300 flex flex-col items-center justify-center gap-1 text-xs">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            {busy ? "…" : "Add"}
          </button>
        )}
      </div>
      <p className="text-[11px] text-slate-500 mt-1.5">First photo is the cover · up to {max}</p>
    </div>
  );
}

export default MultiImageUpload;
